// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, CheckBox, FormField } from 'grommet'
import { PropTypes } from 'prop-types'
import { Copy } from 'grommet-icons'
import lodashGet from 'lodash/get'

import { generateRedirectUrl } from '../../../../../constants'
import { Typography } from '../../../../../../../../components'
import { handleCopy, defaultFieldsError } from '../../utils/page-utils'
import { WizardContext } from '../../../../../../../../components/wizard/WizardContext'

const LoginClientSetUp = ({ oidcIssuerUrl, onRedirectUrlChange }) => {
  const { t } = useTranslation(['common', 'iam', 'authn'])
  const { formValues, setFormValues, attemptedAdvance, setAttemptedAdvance } =
    useContext(WizardContext)
  const [redirectUrl, setRedirectUrl] = useState('')
  const [fieldsError, setFieldsError] = useState(defaultFieldsError)

  useEffect(() => {
    const url = generateRedirectUrl(oidcIssuerUrl)
    setRedirectUrl(url)

    if (onRedirectUrlChange) {
      onRedirectUrlChange(url) // Call the parent callback with the new URL
    }
  }, [oidcIssuerUrl, onRedirectUrlChange])

  useEffect(() => {
    // Validate clientId and client_secret when user attempts to advance
    if (attemptedAdvance) {
      const clientID = lodashGet(formValues, 'oidcIdpConfig.clientId', '')
      const clientSecret = lodashGet(
        formValues,
        'oidcIdpConfig.clientSecret',
        ''
      )

      // Construct a new error state
      const newFieldsError = {
        clientId: clientID ? '' : t('common:required'),
        clientSecret: clientSecret ? '' : t('common:required')
      }

      // Update the error state with both validations
      setFieldsError((prevFieldsError) => ({
        ...prevFieldsError,
        ...newFieldsError
      }))

      // Optionally reset `attemptedAdvance` if needed
      setAttemptedAdvance(false)
    }
  }, [attemptedAdvance, formValues, t, setAttemptedAdvance])

  const handleClientIdChange = (event) => {
    const updatedClientId = event.target.value
    setFormValues((prevFormValues) => ({
      ...prevFormValues,
      oidcIdpConfig: {
        ...lodashGet(prevFormValues, 'oidcIdpConfig', {}),
        clientId: updatedClientId
      }
    }))
    setFieldsError((prevFieldsError) => ({
      ...prevFieldsError,
      clientId: ''
    }))
  }

  const handleClientSecretChange = (event) => {
    const updatedClientSecret = event.target.value
    setFormValues((prevFormValues) => ({
      ...prevFormValues,
      oidcIdpConfig: {
        ...lodashGet(prevFormValues, 'oidcIdpConfig', {}),
        clientSecret: updatedClientSecret
      }
    }))
    setFieldsError((prevFieldsError) => ({
      ...prevFieldsError,
      clientSecret: ''
    }))
  }

  const [pkceChecked, setPkceChecked] = useState(
    lodashGet(formValues, 'oidcIdpConfig.usePkce', true) // Default to true
  )

  useEffect(() => {
    // Sync pkceChecked with formValues when the component mounts
    setPkceChecked(lodashGet(formValues, 'oidcIdpConfig.usePkce', true))
  }, [formValues])

  const handlePkceChange = (event) => {
    const isChecked = event.target.checked
    setPkceChecked(isChecked)
    setFormValues((prevFormValues) => ({
      ...prevFormValues,
      oidcIdpConfig: {
        ...lodashGet(prevFormValues, 'oidcIdpConfig', {}),
        usePkce: isChecked
      }
    }))
  }

  useEffect(() => {
    // Ensure `usePkce` is always set to `true` by default
    setFormValues((prevFormValues) => ({
      ...prevFormValues,
      oidcIdpConfig: {
        usePkce: lodashGet(prevFormValues, 'oidcIdpConfig.usePkce', true),
        ...prevFormValues.oidcIdpConfig
      }
    }))
  }, [setFormValues])

  return (
    <Box
      margin={{ top: 'small', bottom: 'medium' }}
      fill
      align="left"
      justify="start"
    >
      <Typography
        type="text"
        size="xsmall"
        weight="500"
        testId="redirect_url_label"
      >
        {t('iam:sso_profiles_oidc.greenlake_redirect_label')}
      </Typography>
      <Typography
        type="text"
        size="xsmall"
        testId="oidc_client_field_description"
      >
        {t('iam:sso_profiles_oidc.oidc_client_field_description')}
      </Typography>
      <Box
        direction="row"
        align="center"
        gap="small"
        data-testid="redirect_url_section"
      >
        <Box
          gap="small"
          background="light-2"
          pad="small"
          margin={{ top: 'medium', bottom: 'medium' }}
          round="xsmall"
          overflow="auto"
        >
          <Typography
            type="text"
            size="small"
            weight="400"
            testId="redirect_url"
          >
            {redirectUrl}
          </Typography>
        </Box>
        <Copy
          onClick={() => handleCopy(redirectUrl)}
          style={{ cursor: 'pointer' }}
        />
      </Box>
      <Box
        direction="row"
        align="center"
        gap="small"
        data-testid="oidc_client_id"
      >
        <FormField
          name="client_id"
          label={t('iam:sso_profiles_oidc.client_id_label')}
          width="large"
          required
          value={lodashGet(formValues, 'oidcIdpConfig.clientId', '')}
          onChange={handleClientIdChange}
          error={fieldsError.clientId}
        />
      </Box>
      <Box
        direction="row"
        align="center"
        gap="small"
        data-testid="oidc_client_secret"
      >
        <FormField
          type="password"
          name="client_secret"
          label={t('iam:sso_profiles_oidc.client_secret_label')}
          testId="oidc-client-secret"
          width="large"
          required
          value={lodashGet(formValues, 'oidcIdpConfig.clientSecret', '')}
          onChange={handleClientSecretChange}
          error={fieldsError.clientSecret}
        />
      </Box>
      <CheckBox
        label={t('iam:sso_profiles_oidc.pkce_label')}
        checked={pkceChecked}
        onChange={handlePkceChange}
      />
    </Box>
  )
}

LoginClientSetUp.propTypes = {
  oidcIssuerUrl: PropTypes.string.isRequired,
  onRedirectUrlChange: PropTypes.func.isRequired
}

export default LoginClientSetUp
